import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, ChevronRight, Award, Clock, BookMarked, Users,
  Target, BookOpen, Briefcase, Hospital, FlaskConical,
  GraduationCap, Globe, Scale, CheckCircle2, ChevronDown, ChevronUp, Loader
} from 'lucide-react';
import { fetchDosen, fetchStrukturOrganisasi, fetchStudyProgram, getImageUrl } from '../utils/api';
import vmts from '../data/vmts-20260820.json';

const PROGRAMS = {
  kesmas: {
    name: 'S1 Kesehatan Masyarakat',
    kaprodiJabatan: 'Ka. Prodi Kesehatan Masyarakat',
    sekprodiJabatan: 'Sek. Prodi Kesmas',
    abbr: 'Kesmas',
    degree: 'Sarjana Kesehatan Masyarakat (SKM)',
    level: 'Strata 1',
    accreditation: 'BAIK SEKALI',
    accreditationDesc: 'SK LAM-PTKes No. 0418/LAM-PTKes/Akr/Sar/XI/2020',
    sks: 144,
    duration: '4 Tahun (8 Semester)',
    students: 66,
    colorHero: 'from-orange-700 via-amber-500 to-orange-400',
    colorBadge: 'bg-orange-100 text-orange-700',
    image: 'https://images.unsplash.com/photo-1659718282278-fd3ba02cb782?w=1200&q=80',
    visi: 'Menjadi Program Studi Kesehatan Masyarakat yang unggul dalam pemberdayaan masyarakat agroindustri berbasis Public Health Entrepreneurship berdaya saing Global Tahun 2045.',
    misiIntro: 'Misi Program Studi Kesehatan Masyarakat berbunyi:',
    misi: [
      'Menyelenggarakan pendidikan kesehatan masyarakat yang berbasis bidang pemberdayaan masyarakat agroindustri berbasis Public Health Entrepreneurship.',
      'Mengembangkan inovasi bidang kesehatan masyarakat yang menghasilkan produk dan jasa yang bernilai guna bagi masyarakat.',
      'Melaksanakan program masyarakat melalui program pemberdayaan masyarakat dan usaha kesehatan mandiri.',
      'Menyelenggarakan pengalaman akademik yang berjiwa healthpreneur dengan memanfaatkan digital kesehatan dalam layanan kesehatan masyarakat.',
    ],
    tujuanIntro: 'Tujuan Program Studi Kesehatan Masyarakat berbunyi:',
    tujuan: [
      'Menghasilkan lulusan yang kompeten menyelesaikan masalah yang kompleks dalam bidang kesehatan masyarakat berdaya saing global.',
      'Menghasilkan lulusan yang inovatif dalam pengembangan ilmu kesehatan masyarakat.',
      'Menghasilkan lulusan yang komunikatif, responsif, adaptif dan terampil dalam program pemberdayaan masyarakat.',
      'Menghasilkan public health entrepreneur yang profesional berdaya saing global.',
    ],
    sasaranIntro: 'Sasaran Program Studi Kesehatan Masyarakat berbunyi:',
    sasaran: [
      'Terwujudnya lulusan yang kompeten menyelesaikan masalah yang kompleks dalam bidang kesehatan masyarakat berdaya saing global.',
      'Terwujudnya lulusan yang inovatif dalam pengembangan ilmu kesehatan masyarakat.',
      'Terwujudnya lulusan yang komunikatif, responsif, adaptif dan terampil dalam program pemberdayaan masyarakat.',
      'Terwujudnya public health entrepreneur yang profesional berdaya saing global.',
    ],
    maknaVisi: [
      {
        title: 'Unggul dalam Bidang Pemberdayaan Masyarakat Agroindustri',
        points: [
          'Unggul dalam mewujudkan pelaksanaan tridharma khususnya dalam aspek pengabdian kepada masyarakat yang berkelanjutan dalam upaya meningkatkan derajat kesehatan masyarakat sektor pertanian.',
        ]
      },
      {
        title: 'Berbasis Public Health Entrepreneurship',
        points: [
          'Mengintegrasikan prinsip kewirausahaan dan ilmu kesehatan masyarakat dalam proses pembelajaran untuk menghasilkan lulusan yang inovatif, mandiri serta mampu mengembangkan usaha dan solusi kesehatan yang berdampak.',
        ]
      },
      {
        title: 'Berdaya Saing Global',
        points: [
          'Kemampuan program studi menghasilkan lulusan, inovasi, dan karya ilmiah yang berkualitas, adaptif terhadap perkembangan global dengan tetap mengangkat kearifan lokal.',
        ]
      },
    ],
    kompetensi: [
      'Pemberdayaan masyarakat agroindustri dan peningkatan kesehatan masyarakat sektor pertanian',
      'Public health entrepreneurship untuk mengembangkan usaha dan solusi kesehatan yang berdampak',
      'Inovasi produk dan jasa kesehatan masyarakat yang bernilai guna',
      'Pemanfaatan digital kesehatan dalam layanan dan program kesehatan masyarakat',
      'Penyelesaian masalah kesehatan masyarakat yang kompleks secara komunikatif, responsif, dan adaptif',
    ],
    curriculum: [
      { sem: 1, subjects: ['Pancasila & Kewarganegaraan', 'Bahasa Indonesia', 'Pengantar Kesehatan Masyarakat', 'Ilmu Gizi Dasar', 'Biologi Dasar', 'Kimia Dasar', 'Bahasa Inggris Kesehatan'] },
      { sem: 2, subjects: ['Epidemiologi Dasar', 'Biostatistik Deskriptif', 'Kesehatan Lingkungan Dasar', 'Promosi Kesehatan I', 'Administrasi & Kebijakan Kesehatan', 'Sosio-Antropologi Kesehatan'] },
      { sem: 3, subjects: ['Epidemiologi Lanjut', 'Biostatistik Inferensial', 'Manajemen Penyakit Menular', 'Gizi Masyarakat', 'K3 (Keselamatan & Kesehatan Kerja)', 'Promosi Kesehatan II'] },
      { sem: 4, subjects: ['Surveilans Epidemiologi', 'Sistem Informasi Kesehatan', 'Manajemen Rumah Sakit', 'Kesehatan Ibu & Anak', 'Demografi Kesehatan', 'Kewirausahaan Kesehatan'] },
      { sem: 5, subjects: ['Metodologi Penelitian', 'Evaluasi Program Kesehatan', 'Manajemen Bencana & KLB', 'Kesehatan Reproduksi', 'Gizi Klinik', 'Epidemiologi Gizi'] },
      { sem: 6, subjects: ['Seminar Proposal', 'PKL Puskesmas/Dinas', 'Analisis Kebijakan Kesehatan', 'Epidemiologi PTM', 'Manajemen Puskesmas'] },
      { sem: 7, subjects: ['Penelitian Kesehatan Masyarakat', 'Magang Instansi Kesehatan', 'Manajemen Data Kesehatan', 'Kapita Selekta Kesehatan'] },
      { sem: 8, subjects: ['Skripsi', 'Ujian Komprehensif'] },
    ],
    staff: [
      { name: 'Dr. Raden Ayu Kusuma Dewi, SKM, M.Kes', nidn: '0201018501', edu: 'S3 Kesehatan Masyarakat — UI', expertise: 'Epidemiologi & Biostatistik' },
      { name: 'Dr. Ahmad Fauzi, SKM, M.Epid', nidn: '0315077802', edu: 'S3 Epidemiologi — UNAIR', expertise: 'Epidemiologi Penyakit Menular' },
      { name: 'Hesti Nugrahaeni, SKM, M.Kes', nidn: '0410088903', edu: 'S2 Gizi Masyarakat — IPB', expertise: 'Gizi Masyarakat & KIA' },
      { name: 'Drs. Bambang Setiawan, M.Kes', nidn: '0502066704', edu: 'S2 Kesehatan Lingkungan — UNDIP', expertise: 'Kesehatan Lingkungan & K3' },
      { name: 'Rizka Amalia, SKM, MKM', nidn: '0711029005', edu: 'S2 Kebijakan Kesehatan — UI', expertise: 'Administrasi & Kebijakan Kesehatan' },
    ],
    careers: [
      { icon: <Target size={20} />, title: 'Epidemiolog Kesehatan', desc: 'Bekerja di Dinas Kesehatan, Kementerian Kesehatan, atau WHO dalam pengendalian dan pengawasan penyakit.' },
      { icon: <Hospital size={20} />, title: 'Manajer Rumah Sakit', desc: 'Mengelola administrasi, operasional, dan peningkatan mutu pelayanan fasilitas kesehatan.' },
      { icon: <Globe size={20} />, title: 'Health Promotion Officer', desc: 'Merancang dan melaksanakan program promosi kesehatan di masyarakat atau perusahaan.' },
      { icon: <FlaskConical size={20} />, title: 'Peneliti Kesehatan', desc: 'Melakukan riset kesehatan masyarakat di lembaga penelitian, universitas, atau NGO kesehatan.' },
      { icon: <Briefcase size={20} />, title: 'Konsultan Kesehatan', desc: 'Memberikan konsultasi program kesehatan kepada perusahaan, pemerintah, atau lembaga internasional.' },
    ],
  },

  keperawatan: {
    name: 'S1 Keperawatan',
    kaprodiJabatan: 'Ka. Prodi Ilmu Keperawatan',
    sekprodiJabatan: 'Sek. Prodi Keperawatan',
    abbr: 'Keperawatan',
    degree: 'Sarjana Keperawatan (S.Kep)',
    level: 'Strata 1',
    accreditation: 'BAIK SEKALI',
    accreditationDesc: 'SK LAM-PTKes Nomor: 0887/LAM-PTKes/Akr/Sar/XII/2023',
    sks: 148,
    duration: '4 Tahun (8 Semester)',
    students: 706,
    colorHero: 'from-emerald-800 via-emerald-600 to-teal-500',
    colorBadge: 'bg-emerald-100 text-emerald-700',
    image: 'https://images.unsplash.com/photo-1659353888906-adb3e0041693?w=1200&q=80',
    visi: 'Menjadi Program Studi Ners yang Terdepan dalam menghasilkan Perawat Profesional dalam bidang Keperawatan Medikal Bedah kekhususan perawatan luka berbasis IPTEKS di kopertis Wilayah X tahun Tahun 2025.',
    misi: [
      'Menyelenggarakan program pendidikan dan pengajaran yang mengacu pada standar nasional, dengan penekanan keperawatan medikal bedah kekhususan perawatan luka yang berbasis IPTEKS.',
      'Mengembangkan riset keperawatan dan publikasi ilmiah yang berkualitas dengan penekanan kepada keperawatan medikal bedah kekhususan perawatan luka yang berbasis IPTEKS.',
      'Menyelenggarakan pengabdian kepada masyarakat berlandaskan kaidah-kaidah ilmu keperawatan dalam upaya meningkatkan derajat kesehatan masyarakat dengan penekanan keperawatan medikal bedah kekhususan perawatan luka yang berbasis IPTEKS.'
    ],
    tujuan: [
      'Menghasilkan lulusan yang mampu menerapkan pengetahuan dan keterampilan di bidang keperawatan dengan unggulan Keperawatan Medikal Bedah kekhususan Keperawatan Luka yang dibimbing oleh tenaga pengajar lulusan dalam dan luar negeri.',
      'Lulusan prodi ini memiliki profil sebagai Care Provider, Community Leader, Educator, Manager, Researcher.',
      'Lulusan program studi ini telah banyak yang bekerja di Rumah Sakit pemerintah dan swasta, Puskesmas, Dinas Kesehatan, layanan kesehatan di berbagai perusahaan swasta, dan Dosen di berbagai kampus Negeri maupun Swasta.'
    ],
    kompetensi: [
      'Pemberian asuhan keperawatan holistik dan komprehensif berbasis evidence',
      'Pengelolaan asuhan keperawatan pada individu, keluarga, dan komunitas',
      'Pendidikan kesehatan dan konseling kepada pasien dan keluarga',
      'Manajemen dan kepemimpinan unit pelayanan keperawatan',
      'Riset dan pengembangan ilmu keperawatan',
    ],
    curriculum: [
      { sem: 1, subjects: ['Anatomi & Fisiologi', 'Konsep Dasar Keperawatan', 'Ilmu Biomedik', 'Komunikasi Keperawatan', 'Pancasila & Agama', 'Bahasa Inggris'] },
      { sem: 2, subjects: ['Patofisiologi', 'Farmakologi Keperawatan', 'Keperawatan Dasar', 'Kebutuhan Dasar Manusia', 'Psikologi Kesehatan', 'Dokumentasi Keperawatan'] },
      { sem: 3, subjects: ['Keperawatan Medikal Bedah I', 'Keperawatan Anak I', 'Keperawatan Maternitas I', 'Keperawatan Komunitas I', 'Metodologi Keperawatan'] },
      { sem: 4, subjects: ['Keperawatan Medikal Bedah II', 'Keperawatan Jiwa', 'Keperawatan Gerontik', 'Keperawatan Gawat Darurat I', 'Bioetik Keperawatan'] },
      { sem: 5, subjects: ['Keperawatan Kritis', 'Keperawatan Gawat Darurat II', 'Manajemen Keperawatan', 'Keperawatan Paliatif', 'Metodologi Penelitian'] },
      { sem: 6, subjects: ['Seminar Proposal', 'Praktik Klinik Komprehensif I', 'Keperawatan Medikal Bedah III', 'Analisis & Informasi Kesehatan'] },
      { sem: 7, subjects: ['Praktik Klinik Komprehensif II', 'Manajemen Bangsal', 'Kewirausahaan Keperawatan', 'Kapita Selekta Keperawatan'] },
      { sem: 8, subjects: ['Skripsi', 'Ujian OSCE', 'Ujian Komprehensif'] },
    ],
    staff: [
      { name: 'Ns. Dr. Sri Wahyuni, S.Kep, M.Kep', nidn: '0305027601', edu: 'S3 Keperawatan — UI', expertise: 'Keperawatan Medikal Bedah' },
      { name: 'Ns. Hendri Pratama, S.Kep, M.Kep, Sp.KMB', nidn: '0118038202', edu: 'Sp. KMB — UNPAD', expertise: 'Keperawatan Kritis & KMB' },
      { name: 'Ns. Dewi Lestari, S.Kep, M.Kep, Sp.Kep.J', nidn: '0221089003', edu: 'Sp. Keperawatan Jiwa — UI', expertise: 'Keperawatan Jiwa & Psikiatri' },
      { name: 'Ns. Rini Andriani, S.Kep, M.Kes', nidn: '0907087704', edu: 'S2 Kesehatan Komunitas — UNDIP', expertise: 'Keperawatan Komunitas & Gerontik' },
      { name: 'Ns. Budi Santoso, S.Kep, M.Kep', nidn: '0303059105', edu: 'S2 Keperawatan — UNAIR', expertise: 'Keperawatan Anak & Maternitas' },
      { name: 'Ns. Aisyah Putri Rahma, S.Kep, M.Kep', nidn: '0612099206', edu: 'S2 Keperawatan Gawat Darurat — USU', expertise: 'Keperawatan Gawat Darurat & Kritis' },
    ],
    careers: [
      { icon: <Hospital size={20} />, title: 'Perawat Klinis Rumah Sakit', desc: 'Bekerja di unit rawat inap, ICU, UGD, atau unit spesialis di rumah sakit negeri dan swasta.' },
      { icon: <Globe size={20} />, title: 'Perawat Komunitas (Puskesmas)', desc: 'Bertugas di Puskesmas, klinik komunitas, atau program home care di masyarakat.' },
      { icon: <Users size={20} />, title: 'Manajer Keperawatan', desc: 'Memimpin tim keperawatan, menjadi kepala ruangan, atau kepala bidang keperawatan.' },
      { icon: <GraduationCap size={20} />, title: 'Pendidik Keperawatan', desc: 'Mengajar di institusi pendidikan keperawatan atau menjadi clinical instructor rumah sakit.' },
      { icon: <FlaskConical size={20} />, title: 'Peneliti Keperawatan', desc: 'Melakukan riset evidence-based untuk pengembangan ilmu dan praktik keperawatan.' },
    ],
  },

  farmasi: {
    name: 'S1 Farmasi',
    kaprodiJabatan: 'Ka. Prodi Farmasi',
    sekprodiJabatan: 'Sek. Prodi Farmasi',
    abbr: 'Farmasi',
    degree: 'Sarjana Farmasi (S.Farm)',
    level: 'Strata 1',
    accreditation: 'BAIK SEKALI',
    accreditationDesc: 'SK LAM-PTKes Nomor: 0670/LAM-PTKes/Akr/Sar/Vlll/2022',
    sks: 144,
    duration: '4 Tahun (8 Semester)',
    students: 521,
    colorHero: 'from-blue-800 via-blue-600 to-blue-500',
    colorBadge: 'bg-blue-100 text-blue-700',
    image: 'https://images.unsplash.com/photo-1766297248027-611186dbccc1?w=1200&q=80',
    visi: 'Menjadi Program Studi Farmasi yang unggul dalam sains dan inovasi kefarmasian berbasis pharmapreneurship dan kearifan lokal untuk menghasilkan lulusan yang berdaya saing global pada tahun 2045.',
    misiIntro: 'Misi Program Studi Farmasi berbunyi:',
    misi: [
      'Menyelenggarakan pembelajaran kefarmasian berbasis sains dan teknologi yang inovatif berorientasi pada pharmapreneurship.',
      'Mengembangkan riset kefarmasian berbasis pharmapreneurship dan kearifan lokal yang bernilai guna dan berdaya saing global.',
      'Mengembangkan kolaborasi strategis dengan masyarakat dan atau industri dalam meningkatkan kualitas kesehatan dan kemandirian usaha dibidang farmasi.'
    ],
    tujuanIntro: 'Tujuan Program Studi Farmasi berbunyi:',
    tujuan: [
      'Menghasilkan lulusan farmasi yang kompeten, inovatif dan berjiwa pharmapreneur.',
      'Menghasilkan luaran riset kefarmasian berbasis pharmapreneurship dan kearifan lokal yang aplikatif dan berdaya saing global.',
      'Menghasilkan kegiatan kolaborasi dengan masyarakat dan atau industri dalam peningkatan kualitas kesehatan maupun kemandirian usaha.'
    ],
    sasaranIntro: 'Sasaran Program Studi Farmasi berbunyi:',
    sasaran: [
      'Terwujudnya lulusan farmasi yang kompeten dibidang kefarmasian dan berjiwa pharmapreneur.',
      'Terwujudnya luaran riset kefarmasian inovatif berbasis pharmapreneurship dan kearifan lokal yang aplikatif dan berdaya saing global.',
      'Terwujudnya pengabdian kefarmasian yang berdampak pada masyarakat berbasis pharmapreneurship dan kearifan lokal.',
      'Terlaksananya kerjasama dengan masyarakat dan atau industri dalam bidang kefarmasian.'
    ],
    kompetensi: [
      'Penerapan sains dan teknologi inovatif dalam pengembangan produk kefarmasian',
      'Kemampuan pharmapreneurship untuk menciptakan peluang dan mengelola usaha farmasi',
      'Pengembangan produk kefarmasian dan obat tradisional berbasis kearifan lokal',
      'Penerapan standar mutu internasional untuk menjamin kualitas produk dan layanan',
      'Kolaborasi strategis dengan masyarakat dan industri di bidang kesehatan',
    ],
    maknaVisi: [
      {
        title: 'Unggul dalam Sains dan Inovasi Kefarmasian',
        points: [
          'Menegaskan bahwa program studi berorientasi pada kualitas standar yang memiliki keunggulan spesifik yang diakui melalui capaian akademik, inovasi, kerja sama, serta luaran lulusan yang kompetitif.',
          'Fokus pada "sains dan inovasi kefarmasian" menunjukkan penguatan pada penguasaan ilmu dasar kefarmasian sekaligus kemampuan mengembangkan produk, teknologi, dan layanan farmasi yang adaptif terhadap perkembangan zaman.'
        ]
      },
      {
        title: 'Berbasis Pharmapreneurship',
        points: [
          'Menekankan bahwa lulusan farmasi mampu memahami dan mengaplikasikan ilmu obat, mengidentifikasi peluang, menciptakan nilai tambah, serta mengembangkan produk atau layanan farmasi yang inovatif dan bernilai ekonomi di bidang kefarmasian, seperti produksi obat herbal, kosmetik, suplemen kesehatan, maupun layanan farmasi berbasis teknologi.'
        ]
      },
      {
        title: 'Kearifan Lokal',
        points: [
          'Menunjukkan bahwa pengembangan ilmu dan praktik kefarmasian tetap memperhatikan potensi dan nilai-nilai lokal. Hal ini dapat diwujudkan melalui pemanfaatan bahan alam, obat tradisional, serta pengetahuan lokal yang dikaji secara ilmiah, sehingga menjadi keunggulan khas yang memiliki nilai tambah.'
        ]
      },
      {
        title: 'Berdaya Saing Global',
        points: [
          'Produk dan lulusan yang dihasilkan mampu memenuhi standar mutu internasional, memiliki kualitas yang kompetitif, serta dapat bersaing dan diakui di tingkat global.'
        ]
      },
      {
        title: 'Tahun 2045',
        points: [
          'Merupakan target waktu memberikan arah strategis jangka panjang yang terukur, sekaligus selaras dengan momentum Indonesia Emas 2045. Hal ini menandakan bahwa seluruh upaya pengembangan program studi direncanakan secara bertahap, berkelanjutan, dan terarah untuk mencapai posisi unggul pada waktu yang telah ditentukan.'
        ]
      }
    ],
    curriculum: [
      { sem: 1, subjects: ['Kimia Dasar', 'Biologi Sel & Molekuler', 'Fisika Dasar', 'Matematika & Statistik Farmasi', 'Bahasa Inggris Farmasi', 'Pancasila & Kewarganegaraan'] },
      { sem: 2, subjects: ['Kimia Organik Farmasi', 'Botani & Farmakognosi Dasar', 'Anatomi & Fisiologi', 'Pengantar Ilmu Farmasi', 'Kimia Analitik Farmasi'] },
      { sem: 3, subjects: ['Farmakognosi & Fitokimia', 'Kimia Medisinal', 'Farmakokinetik I', 'Teknologi Farmasi Dasar', 'Mikrobiologi Farmasi', 'Biokimia'] },
      { sem: 4, subjects: ['Farmakologi I', 'Teknologi Sediaan Solid', 'Teknologi Sediaan Liquid & Semisolid', 'Farmakokinetik II', 'Toksikologi', 'Analisis Farmasi'] },
      { sem: 5, subjects: ['Farmakologi II', 'Farmasi Klinik I', 'Teknologi Sediaan Steril', 'Fitofarmaka & Herbal Medicine', 'Manajemen Farmasi & Apotek'] },
      { sem: 6, subjects: ['Farmasi Klinik II & Farmakoterapi', 'Farmasi Industri', 'Analisis Instrumen', 'Metodologi Penelitian', 'Regulasi Obat & Kosmetik (BPOM)'] },
      { sem: 7, subjects: ['Seminar Proposal', 'Praktik Kerja Profesi Pra-Apoteker', 'Biofarmasetik & Farmakodinamik', 'Farmakoekonomik', 'Kewirausahaan Farmasi'] },
      { sem: 8, subjects: ['Skripsi', 'Ujian Komprehensif', 'Penilaian Praktikum Akhir'] },
    ],
    staff: [
      { name: 'Dr. apt. Marlina Sari, S.Farm, M.Si', nidn: '0405027801', edu: 'S3 Farmasi — ITB', expertise: 'Farmakognosi & Fitokimia' },
      { name: 'apt. Irwan Kusuma, S.Farm, M.Farm', nidn: '0706038302', edu: 'S2 Farmasi Klinik — UNAIR', expertise: 'Farmasi Klinik & Farmakokinetik' },
      { name: 'apt. Nur Aini Handayani, S.Farm, M.Farm.Klin', nidn: '0210088903', edu: 'S2 Farmasi Klinik — UI', expertise: 'Farmasi Klinik & Farmakoterapi' },
      { name: 'apt. Dr. Eko Prasetyo, S.Farm, M.Si', nidn: '0319077204', edu: 'S3 Kimia Farmasi — UGM', expertise: 'Kimia Medisinal & Analitik Farmasi' },
      { name: 'apt. Feni Rosita Indrawati, S.Farm, M.Farm', nidn: '0811059005', edu: 'S2 Bioteknologi Farmasi — ITS', expertise: 'Teknologi Sediaan & Formulasi Farmasi' },
    ],
    careers: [
      { icon: <FlaskConical size={20} />, title: 'Apoteker (Setelah Profesi)', desc: 'Mengelola apotek komunitas atau bertugas sebagai apoteker penanggungjawab di rumah sakit setelah lulus profesi.' },
      { icon: <Hospital size={20} />, title: 'Industri Farmasi & Kosmetik', desc: 'Bekerja di R&D, produksi, pengawasan mutu, atau regulatory affairs perusahaan farmasi.' },
      { icon: <Target size={20} />, title: 'Peneliti Farmasi', desc: 'Mengembangkan obat baru, herbal medicine, nutrasetikal, atau bioteknologi farmasi.' },
      { icon: <Scale size={20} />, title: 'Regulator BPOM', desc: 'Menjadi pengawas obat, makanan, dan kosmetik di BPOM atau Kementerian Kesehatan.' },
      { icon: <Briefcase size={20} />, title: 'Konsultan Kefarmasian', desc: 'Memberikan konsultasi pelayanan farmasi kepada fasilitas kesehatan atau industri.' },
    ],
  },

  ners: {
    name: 'Profesi Ners',
    kaprodiJabatan: 'Ka. Prodi Profesi Ners',
    sekprodiJabatan: 'Sek. Prodi Profesi Ners',
    abbr: 'Ners',
    degree: 'Ners (Ns.)',
    level: 'Profesi',
    accreditation: 'BAIK SEKALI',
    accreditationDesc: 'SK LAM-PTKes No. 0888/LAM-PTKes/Akr/Pro/XII/2023',
    sks: 36,
    duration: '2 Semester (1 Tahun)',
    students: 72,
    colorHero: 'from-rose-800 via-red-600 to-orange-500',
    colorBadge: 'bg-red-100 text-red-700',
    image: 'https://images.pexels.com/photos/35645528/pexels-photo-35645528.jpeg?w=1200',
    visi: 'Menjadi Program Studi Ners yang Terdepan dalam menghasilkan Perawat Profesional dalam bidang Keperawatan Medikal Bedah kekhususan perawatan luka berbasis IPTEKS di kopertis Wilayah X tahun Tahun 2025.',
    misi: [
      'Menyelenggarakan program pendidikan dan pengajaran yang mengacu pada standar nasional, dengan penekanan keperawatan medikal bedah kekhususan perawatan luka yang berbasis IPTEKS.',
      'Mengembangkan riset keperawatan dan publikasi ilmiah yang berkualitas dengan penekanan kepada keperawatan medikal bedah kekhususan perawatan luka yang berbasis IPTEKS.',
      'Menyelenggarakan pengabdian kepada masyarakat berlandaskan kaidah-kaidah ilmu keperawatan dalam upaya meningkatkan derajat kesehatan masyarakat dengan penekanan keperawatan medikal bedah kekhususan perawatan luka yang berbasis IPTEKS.'
    ],
    tujuan: [
      'Menghasilkan lulusan yang mampu menerapkan pengetahuan dan keterampilan di bidang keperawatan dengan unggulan Keperawatan Medikal Bedah kekhususan Keperawatan Luka yang dibimbing oleh tenaga pengajar lulusan dalam dan luar negeri.',
      'Lulusan prodi ini memiliki profil sebagai Care Provider, Community Leader, Educator, Manager, Researcher.',
      'Lulusan program studi ini telah banyak yang bekerja di Rumah Sakit pemerintah dan swasta, Puskesmas, Dinas Kesehatan, layanan kesehatan di berbagai perusahaan swasta, dan Dosen di berbagai perguruan tinggi.'
    ],
    kompetensi: [
      'Asuhan keperawatan profesional di semua area praktik klinik',
      'Pengambilan keputusan klinik berbasis evidence dan etika',
      'Komunikasi terapeutik efektif dengan pasien, keluarga, dan tim',
      'Kolaborasi interprofesi dalam tim kesehatan multidisiplin',
      'Manajemen dan kepemimpinan klinis di unit layanan kesehatan',
    ],
    curriculum: [
      { sem: 1, subjects: ['Stase Keperawatan Medikal Bedah I & II', 'Stase Keperawatan Anak', 'Stase Keperawatan Maternitas', 'Stase Keperawatan Jiwa'] },
      { sem: 2, subjects: ['Stase Keperawatan Komunitas & Keluarga', 'Stase Keperawatan Gerontik', 'Stase Keperawatan Gawat Darurat & Kritis', 'Stase Manajemen Keperawatan', 'Uji Kompetensi Ners (UKOM)'] },
    ],
    staff: [
      { name: 'Ns. Dr. Sri Wahyuni, S.Kep, M.Kep', nidn: '0305027601', edu: 'S3 Keperawatan — UI', expertise: 'Koordinator Program Profesi Ners' },
      { name: 'Ns. Hendri Pratama, S.Kep, M.Kep, Sp.KMB', nidn: '0118038202', edu: 'Sp. KMB — UNPAD', expertise: 'Pembimbing Klinik KMB & Kritis' },
      { name: 'Ns. Dewi Lestari, S.Kep, M.Kep, Sp.Kep.J', nidn: '0221089003', edu: 'Sp. Keperawatan Jiwa — UI', expertise: 'Pembimbing Klinik Keperawatan Jiwa' },
    ],
    careers: [
      { icon: <Hospital size={20} />, title: 'Ners Klinis Rumah Sakit', desc: 'Bekerja sebagai perawat profesional di unit rawat inap, ICU, UGD, atau unit spesialis.' },
      { icon: <GraduationCap size={20} />, title: 'Ners Spesialis (Lanjutan)', desc: 'Melanjutkan ke program spesialis keperawatan untuk mengembangkan keahlian di bidang tertentu.' },
      { icon: <Globe size={20} />, title: 'Ners Komunitas', desc: 'Bertugas di Puskesmas, klinik pratama, atau program perawatan berbasis rumah (home care).' },
      { icon: <Users size={20} />, title: 'Manajer Keperawatan', desc: 'Menjadi kepala ruangan, manajer keperawatan, atau pimpinan pelayanan di fasilitas kesehatan.' },
    ],
  },

  apoteker: {
    name: 'Profesi Apoteker',
    comingSoon: true,
    kaprodiJabatan: 'Ka. Prodi Profesi Apoteker',
    sekprodiJabatan: 'Sek. Prodi Apoteker',
    abbr: 'Apoteker',
    degree: 'Apoteker (Apt.)',
    level: 'Profesi',
    accreditation: 'B',
    sks: 36,
    duration: '2 Semester (1 Tahun)',
    students: 120,
    colorHero: 'from-purple-900 via-purple-700 to-violet-500',
    colorBadge: 'bg-purple-100 text-purple-700',
    image: 'https://images.pexels.com/photos/9628853/pexels-photo-9628853.jpeg?w=1200',
    visi: 'Menghasilkan apoteker yang profesional, berkompetensi tinggi, dan beretika dalam pelayanan kefarmasian klinis, komunitas, dan industri.',
    misi: [
      'Menyelenggarakan pendidikan profesi apoteker melalui Praktik Kerja Profesi Apoteker (PKPA) di berbagai wahana terakreditasi.',
      'Membekali calon apoteker dengan kompetensi pelayanan farmasi klinik yang terstandar IAI dan kebutuhan industri.',
      'Membangun budaya profesionalisme dan etika kefarmasian yang kuat sejak tahap pendidikan profesi.',
    ],
    kompetensi: [
      'Pelayanan kefarmasian komprehensif di apotek komunitas dan rumah sakit',
      'Konseling, edukasi, dan informasi obat kepada pasien dan tenaga kesehatan',
      'Manajemen apotek, logistik farmasi, dan instalasi farmasi rumah sakit',
      'Pengawasan mutu, regulasi, dan keamanan obat sesuai standar BPOM',
      'Kolaborasi dalam tim pelayanan kesehatan primer dan rujukan',
    ],
    curriculum: [
      { sem: 1, subjects: ['PKPA Apotek Komunitas', 'PKPA Rumah Sakit (IFRS)', 'PKPA Industri Farmasi', 'Farmasi Klinik & Farmakoterapi Terapan'] },
      { sem: 2, subjects: ['PKPA Puskesmas & Klinik', 'PKPA Distribusi & PBF/BPOM', 'Manajemen Farmasi Lanjut', 'Seminar Kasus Klinik & Jurnal Club', 'Uji Kompetensi Apoteker Indonesia (UKAI)'] },
    ],
    staff: [
      { name: 'Dr. apt. Marlina Sari, S.Farm, M.Si', nidn: '0405027801', edu: 'S3 Farmasi — ITB', expertise: 'Koordinator Program Profesi Apoteker' },
      { name: 'apt. Irwan Kusuma, S.Farm, M.Farm', nidn: '0706038302', edu: 'S2 Farmasi Klinik — UNAIR', expertise: 'Pembimbing PKPA RS & Klinik' },
      { name: 'apt. Nur Aini Handayani, S.Farm, M.Farm.Klin', nidn: '0210088903', edu: 'S2 Farmasi Klinik — UI', expertise: 'Pembimbing PKPA Apotek' },
    ],
    careers: [
      { icon: <FlaskConical size={20} />, title: 'Apoteker Apotek Komunitas', desc: 'Memimpin apotek komunitas, memberikan konseling dan pelayanan kefarmasian langsung ke masyarakat.' },
      { icon: <Hospital size={20} />, title: 'Apoteker Rumah Sakit', desc: 'Bertugas di Instalasi Farmasi Rumah Sakit (IFRS) dalam pelayanan farmasi klinik dan rawat inap.' },
      { icon: <Briefcase size={20} />, title: 'Apoteker Industri Farmasi', desc: 'Bekerja di industri farmasi dalam produksi, QC, QA, atau regulatory affairs.' },
      { icon: <Scale size={20} />, title: 'Pengawas BPOM/Kemenkes', desc: 'Menjadi pengawas obat, makanan, dan kosmetik di instansi pemerintah.' },
    ],
  },
};

const TABS = ['Profil Program', 'Kurikulum', 'Dosen Pengajar', 'Prospek Karir'];

const THEME_CLASSES = {
  orange: { colorHero: 'from-orange-700 via-amber-500 to-orange-400', colorBadge: 'bg-orange-100 text-orange-700' },
  emerald: { colorHero: 'from-emerald-800 via-emerald-600 to-teal-500', colorBadge: 'bg-emerald-100 text-emerald-700' },
  blue: { colorHero: 'from-blue-800 via-blue-600 to-blue-500', colorBadge: 'bg-blue-100 text-blue-700' },
  red: { colorHero: 'from-rose-800 via-red-600 to-orange-500', colorBadge: 'bg-red-100 text-red-700' },
  purple: { colorHero: 'from-purple-900 via-purple-700 to-violet-500', colorBadge: 'bg-purple-100 text-purple-700' },
};

const CAREER_ICONS = { Target, Hospital, Globe, FlaskConical, Briefcase, Users, GraduationCap, Scale };

const normalizeProgram = (program) => ({
  ...program,
  image: program.imageUrl,
  ...(THEME_CLASSES[program.theme] || THEME_CLASSES.purple),
});

const imageVariant = (src, width) => {
  const supported = src?.includes('/api/public/program-studi-media/')
    || /\/program-studi\/[a-f0-9-]{36}\.webp(?:\?|$)/i.test(src);
  return supported ? `${src}${src.includes('?') ? '&' : '?'}w=${width}` : src;
};

const ProgramStudiDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [openSem, setOpenSem] = useState(null);

  // State for CMS Dosen
  const [dosenData, setDosenData] = useState([]);
  const [loadingDosen, setLoadingDosen] = useState(true);

  // State for CMS Struktur
  const [strukturData, setStrukturData] = useState([]);
  const [remoteProgram, setRemoteProgram] = useState(null);
  const [loadingProgram, setLoadingProgram] = useState(true);

  useEffect(() => {
    let active = true;
    setRemoteProgram(null);
    setLoadingProgram(true);
    fetchStudyProgram(slug)
      .then((record) => {
        if (active) setRemoteProgram(normalizeProgram(record));
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingProgram(false);
      });

    fetchDosen().then((data) => {
      if (active) {
        setDosenData(data);
        setLoadingDosen(false);
      }
    }).catch(() => active && setLoadingDosen(false));

    fetchStrukturOrganisasi().then((data) => {
      if (active) setStrukturData(data);
    }).catch(() => { });
    return () => { active = false; };
  }, [slug]);

  const prog = remoteProgram || (PROGRAMS[slug] ? { ...PROGRAMS[slug], ...vmts.programs[slug] } : undefined);
  if (!prog && loadingProgram) {
    return <div className="grid min-h-[70vh] place-items-center bg-[#F8F7FF] pt-[70px] text-[#7B2FBE] dark:bg-[#121020]"><Loader className="animate-spin" size={30} /></div>;
  }
  if (!prog) return <Navigate to="/program-studi" replace />;

  const filteredCMS = dosenData.filter(d => d.prodi === slug);
  const dosenList = filteredCMS.length > 0 ? filteredCMS : prog.staff;

  const getInitials = (name) => name?.replace(/^(Dr\.|apt\.|Ns\.|Drs\.|S\.Kep\.)?\s*/, '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '??';

  return (
    <div data-testid="program-studi-detail-page">
      {/* Hero */}
      <div className={`relative pt-[70px] min-h-[320px] bg-gradient-to-br ${prog.colorHero} overflow-hidden`}>
        <img
          src={imageVariant(prog.image, 1600)}
          alt={prog.name}
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                <Home size={14} /> Beranda
              </Link>
              <ChevronRight size={14} />
              <Link to="/program-studi" className="hover:text-white transition-colors">Program Studi</Link>
              <ChevronRight size={14} />
              <span className="text-white">{prog.abbr}</span>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${prog.colorBadge}`}>{prog.level}</span>
              {prog.comingSoon && (
                <span className="px-3 py-0.5 rounded-full text-[10px] font-bold bg-yellow-400 text-yellow-900 tracking-wider shadow-sm animate-pulse">
                  COMING SOON
                </span>
              )}
              <span className="flex items-center gap-1 text-white/70 text-xs">
                <Award size={13} /> Akreditasi {prog.accreditation}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-2">{prog.name}</h1>
            <p className="text-white/80 text-base mb-8">{prog.degree}</p>

            <div className="flex flex-wrap gap-5 text-white/90 text-sm">
              <div className="flex items-center gap-2">
                <BookMarked size={16} className="text-white/60" />
                <span><b className="text-white">{prog.sks}</b> SKS</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-white/60" />
                <span>{prog.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users size={16} className="text-white/60" />
                <span><b className="text-white">{prog.students}+</b> Mahasiswa Aktif</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="sticky top-[70px] z-40 bg-white dark:bg-[#121020] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-0 overflow-x-auto scrollbar-none">
            {TABS.map((tab, i) => (
              <button
                key={tab}
                data-testid={`tab-${i}`}
                onClick={() => setActiveTab(i)}
                className={`flex-shrink-0 px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 ${activeTab === i
                  ? 'border-[#7B2FBE] text-[#7B2FBE] dark:text-[#A855F7]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-[#7B2FBE] hover:border-purple-200'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-[#F8F7FF] dark:bg-[#121020] min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Tab 0: Profil */}
          {activeTab === 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>

              {(() => {
                const kaprodiData = strukturData.find(s => s.jabatan === prog.kaprodiJabatan);
                const sekprodiData = strukturData.find(s => s.jabatan === prog.sekprodiJabatan);

                const matchDosen = (name) => dosenData.find(d => d.nama === name) || {};

                const kaprodiDetails = kaprodiData ? { ...kaprodiData, ...matchDosen(kaprodiData.nama) } : null;
                const sekprodiDetails = sekprodiData ? { ...sekprodiData, ...matchDosen(sekprodiData.nama) } : null;

                if (!kaprodiDetails && !sekprodiDetails) return null;

                return (
                  <div className="mb-10">
                    <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-6 flex items-center gap-2">
                      <Award className="text-[#7B2FBE]" size={26} /> Pimpinan Program Studi
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {[
                        { role: 'Ketua Program Studi', data: kaprodiDetails },
                        { role: 'Sekretaris Program Studi', data: sekprodiDetails }
                      ].filter(x => x.data).map((item, idx) => {
                        const d = item.data;
                        const initials = getInitials(d.nama);
                        return (
                          <div key={idx} className="bg-white dark:bg-[#1A182E] rounded-2xl p-5 border border-purple-100 dark:border-purple-800/30 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#7B2FBE]/5 to-[#9333EA]/5 rounded-bl-full -z-0" />
                            <div className="w-16 h-16 rounded-full border-4 border-purple-50 dark:border-purple-900/40 overflow-hidden shadow-sm flex-shrink-0 bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white text-xl font-bold relative z-10 font-heading">
                              {d.foto ? (
                                <img src={getImageUrl(d.foto)} alt={d.nama} className="w-full h-full object-cover" />
                              ) : initials}
                            </div>
                            <div className="relative z-10 flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[#7B2FBE] dark:text-purple-400 uppercase tracking-widest mb-1 truncate">{item.role}</p>
                              <h4 className="text-base font-bold text-[#1A1A2E] dark:text-white leading-tight mb-0.5 font-heading truncate">{d.nama}</h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{d.nidn ? `NIDN: ${d.nidn}` : ' '}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                );
              })()}

              {prog.pendahuluan && (
                <div className="mb-10 text-gray-700 dark:text-gray-300 leading-relaxed text-[17px] bg-white dark:bg-[#1A182E] p-7 md:p-9 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 relative z-10">
                  <div className="absolute top-0 left-0 w-2 h-full bg-[#7B2FBE] rounded-l-3xl" />
                  {prog.pendahuluan}
                </div>
              )}

              {prog.accreditationDesc && (
                <div className="mb-10 bg-gradient-to-r from-[#7B2FBE] to-[#9333EA] rounded-2xl p-6 md:p-8 text-white shadow-lg relative overflow-hidden flex flex-col md:flex-row items-start md:items-center gap-5">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl pointer-events-none" />
                  <div className="absolute bottom-0 left-10 w-24 h-24 bg-black/10 rounded-full translate-y-1/2 blur-xl pointer-events-none" />
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 backdrop-blur-sm border border-white/30 shadow-inner">
                    <Award size={32} className="text-white drop-shadow-sm" />
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-bold tracking-widest uppercase mb-2 border border-white/20 backdrop-blur-sm">Status Akreditasi</div>
                    <h3 className="text-2xl font-bold font-heading mb-1">{prog.accreditation}</h3>
                    <p className="text-sm text-white/90 leading-relaxed max-w-2xl">{prog.accreditationDesc}</p>
                  </div>
                </div>
              )}

              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-4">Visi Program Studi</h2>
                <div className="bg-white dark:bg-[#1A182E] rounded-3xl p-7 md:p-10 border-l-4 border-[#7B2FBE] shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 right-0 opacity-5 pointer-events-none">
                    <Target size={150} className="translate-x-1/4 -translate-y-1/4 text-[#7B2FBE]" />
                  </div>
                  <p className="text-[#1A1A2E] dark:text-white leading-relaxed italic text-xl font-heading relative z-10">"{prog.visi}"</p>
                </div>
              </div>

              {prog.maknaVisi && (
                <div className="mb-10">
                  <h2 className="text-xl md:text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-5 flex items-center gap-2">
                    <BookOpen className="text-[#7B2FBE] dark:text-purple-400" size={26} /> Makna Tiap Unsur Visi
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {prog.maknaVisi.map((mv, i) => (
                      <div key={i} className={`bg-white dark:bg-[#1A182E] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800/50 transition-all group h-full ${i === prog.maknaVisi.length - 1 && prog.maknaVisi.length % 2 !== 0 ? 'md:col-span-2' : ''}`}>
                        <h3 className="font-bold text-[#1A1A2E] dark:text-white mb-4 text-lg flex items-start gap-3 leading-snug">
                          <span className="bg-gradient-to-br from-[#7B2FBE]/10 to-[#9333EA]/10 dark:from-purple-900/40 dark:to-violet-900/40 border border-purple-100 dark:border-purple-800/50 text-[#7B2FBE] dark:text-purple-300 w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 group-hover:bg-[#7B2FBE] group-hover:text-white transition-colors">{i + 1}</span>
                          <span className="mt-0.5">{mv.title}</span>
                        </h3>
                        <ul className="space-y-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed pl-11">
                          {mv.points.map((p, j) => (
                            <li key={j} className="relative">
                              <span className="absolute -left-4 top-2 w-1.5 h-1.5 rounded-full bg-[#7B2FBE]/50 dark:bg-purple-500/50" />
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-4">Misi Program Studi</h2>
                {prog.misiIntro && <p className="text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">{prog.misiIntro}</p>}
                <div className="space-y-4">
                  {prog.misi.map((m, i) => (
                    <div key={i} className="flex items-start gap-4 bg-white dark:bg-[#1A182E] rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800/50 transition-all group">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7B2FBE]/10 to-[#9333EA]/10 dark:from-[#7B2FBE]/20 dark:to-[#9333EA]/20 flex items-center justify-center text-[#7B2FBE] dark:text-purple-400 font-bold text-sm flex-shrink-0 border border-purple-100 dark:border-purple-800/50 group-hover:bg-[#7B2FBE] group-hover:text-white transition-colors">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mt-0.5">{m}</p>
                    </div>
                  ))}
                </div>
              </div>

              {prog.tujuan && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-4">Tujuan Program Studi</h2>
                  {prog.tujuanIntro && <p className="text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">{prog.tujuanIntro}</p>}
                  <div className="grid grid-cols-1 gap-4">
                    {prog.tujuan.map((t, i) => (
                      <div key={i} className="flex items-start gap-4 bg-white dark:bg-[#1A182E] rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-shadow">
                        <div className="bg-[#7B2FBE]/10 dark:bg-purple-900/30 p-2.5 rounded-xl flex-shrink-0 mt-0.5">
                          <Target size={20} className="text-[#7B2FBE] dark:text-purple-400" />
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{t}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {prog.sasaran && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-4">Sasaran Program Studi</h2>
                  {prog.sasaranIntro && <p className="text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">{prog.sasaranIntro}</p>}
                  <div className="grid grid-cols-1 gap-4">
                    {prog.sasaran.map((s, i) => (
                      <div key={i} className="flex items-start gap-4 bg-white dark:bg-[#1A182E] rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-shadow">
                        <div className="bg-[#7B2FBE]/10 dark:bg-purple-900/30 p-2.5 rounded-xl flex-shrink-0 mt-0.5">
                          <Target size={20} className="text-[#7B2FBE] dark:text-purple-400" />
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{s}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {prog.unggulanTampil && prog.unggulan && (
                <section className="mb-10" aria-labelledby="unggulan-heading" data-testid="prodi-unggulan">
                  <h2 id="unggulan-heading" className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-4">Unggulan</h2>
                  <div className="relative overflow-hidden rounded-3xl bg-[#171230] p-7 md:p-10 text-white shadow-lg ring-1 ring-white/10">
                    {/* Cahaya dari warna tema prodi, supaya serasi dengan hero halaman. */}
                    <div aria-hidden="true" className={`pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-gradient-to-br ${prog.colorHero} opacity-40 blur-3xl`} />
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,#fff_1px,transparent_0)] [background-size:22px_22px]" />
                    <p className="relative max-w-3xl font-heading text-xl font-semibold leading-snug md:text-[1.65rem] md:leading-snug">{prog.unggulan}</p>
                    {prog.unggulanPoin?.length > 0 && (
                      <ul className={`relative mt-8 grid grid-cols-1 gap-3 ${prog.unggulanPoin.length === 3 ? 'sm:grid-cols-3' : prog.unggulanPoin.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
                        {prog.unggulanPoin.map((poin) => (
                          <li key={poin} className="flex items-start gap-3 rounded-2xl border border-white/15 bg-white/[0.07] px-4 py-4 backdrop-blur-sm">
                            <span aria-hidden="true" className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-gradient-to-br ${prog.colorHero}`} />
                            <span className="text-sm font-semibold leading-snug">{poin}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              )}

              <div>
                <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-4">Kompetensi Lulusan</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {prog.kompetensi.map((k, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white dark:bg-[#1A182E] rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800">
                      <CheckCircle2 size={18} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
                      <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{k}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Tab 1: Kurikulum */}
          {activeTab === 1 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">Kurikulum</h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">Total {prog.sks} SKS</span>
              </div>
              <div className="space-y-3">
                {prog.curriculum.map((sem) => (
                  <div key={sem.sem} className="bg-white dark:bg-[#1A182E] rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                    <button
                      data-testid={`sem-toggle-${sem.sem}`}
                      onClick={() => setOpenSem(openSem === sem.sem ? null : sem.sem)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white font-bold text-sm font-heading">
                          {sem.sem}
                        </span>
                        <div>
                          <p className="font-semibold text-[#1A1A2E] dark:text-white text-sm font-heading">Semester {sem.sem}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{sem.subjects.length} Mata Kuliah</p>
                        </div>
                      </div>
                      {openSem === sem.sem ? <ChevronUp size={18} className="text-[#7B2FBE]" /> : <ChevronDown size={18} className="text-gray-400" />}
                    </button>
                    {openSem === sem.sem && (
                      <div className="px-5 pb-4 border-t border-gray-100 dark:border-gray-800">
                        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {sem.subjects.map((subj, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#7B2FBE] flex-shrink-0" />
                              {subj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Tab 2: Dosen Pengajar */}
          {activeTab === 2 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">Dosen Pengajar</h2>
                {!loadingDosen && (
                  <span className="text-sm font-semibold px-4 py-1.5 bg-[#7B2FBE]/10 dark:bg-[#A855F7]/10 text-[#7B2FBE] dark:text-[#A855F7] rounded-full border border-[#7B2FBE]/20">
                    Total: {dosenList.length} Dosen
                  </span>
                )}
              </div>

              {loadingDosen ? (
                <div className="flex items-center justify-center py-10 opacity-60">
                  <Loader className="animate-spin text-[#7B2FBE] mr-2" size={24} /> Memuat data dosen...
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {dosenList.map((s, i) => {
                    // Handle CMS fields vs Hardcoded fields
                    const name = s.nama || s.name;
                    const nidn = s.nidn;
                    const edt = s.pendidikan_terakhir || s.edu || 'S2 Keperawatan / Farmasi';
                    const exp = s.jabatan_fungsional || s.expertise;
                    const foto = s.foto;
                    const id = s.id;
                    const initials = getInitials(name);

                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: i * 0.08 }}
                        data-testid={`staff-card-${i}`}
                        onClick={() => id ? navigate(`/dosen/${id}`) : null}
                        className={`bg-white dark:bg-[#1A182E] rounded-2xl p-5 flex items-start gap-4 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow ${id ? 'cursor-pointer hover:-translate-y-1' : ''}`}
                      >
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white font-bold text-lg flex-shrink-0 font-heading overflow-hidden border border-purple-200 dark:border-purple-800">
                          {foto ? (
                            <img src={getImageUrl(foto)} alt={name} className="w-full h-full object-cover" />
                          ) : initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#1A1A2E] dark:text-white text-sm leading-tight font-heading mb-1">{name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{nidn ? `NIDN: ${nidn}` : ' '}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{edt}</p>
                          {exp && (
                            <span className="inline-block px-2.5 py-0.5 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-[#7B2FBE] dark:text-purple-300 text-xs font-medium truncate max-w-full">
                              {exp}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* Tab 3: Prospek Karir */}
          {activeTab === 3 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-2">Prospek Karir</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">Peluang karir bagi lulusan {prog.name}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {prog.careers.map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    data-testid={`career-card-${i}`}
                    className="bg-white dark:bg-[#1A182E] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7B2FBE]/15 to-[#9333EA]/15 dark:from-purple-900/40 dark:to-violet-900/40 flex items-center justify-center text-[#7B2FBE] dark:text-purple-400 mb-4">
                      {typeof c.icon === 'string'
                        ? (() => {
                          const CareerIcon = CAREER_ICONS[c.icon] || Briefcase;
                          return <CareerIcon size={20} />;
                        })()
                        : c.icon}
                    </div>
                    <h4 className="font-bold text-[#1A1A2E] dark:text-white font-heading mb-2">{c.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{c.desc}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] rounded-2xl p-8 text-center text-white">
                <h3 className="text-xl font-bold font-heading mb-2">Tertarik mendaftar?</h3>
                <p className="text-white/80 text-sm mb-5">Jadilah bagian dari keluarga besar STIKES HI Jambi dan raih masa depan di bidang kesehatan.</p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <Link to="/kontak" className="px-6 py-2.5 rounded-xl bg-white text-[#7B2FBE] font-semibold text-sm hover:bg-purple-50 transition-colors">
                    Hubungi Kami
                  </Link>
                  <a href="https://wa.me/6282177711609" target="_blank" rel="noreferrer"
                    className="px-6 py-2.5 rounded-xl border-2 border-white/50 text-white font-semibold text-sm hover:bg-white/10 transition-colors">
                    WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProgramStudiDetail;
